import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Briefcase, Users, Plus, Camera, Trash2, Edit2, DollarSign, CheckCircle2,
  Clock, Loader2, X, Wrench, PhoneCall, Sparkles, FileText, MoreHorizontal,
  Mail, Phone, CalendarDays,
} from "lucide-react";
import api, { formatCurrency } from "@/lib/api";

/**
 * EmployeesSection
 * --------------------------------------------------------------------------
 * Manages the dealership's non-commissioned staff (mechanics, BDC, detailer,
 * office, etc.) — they earn a fixed WEEKLY SALARY instead of commission.
 *
 * Three responsibilities packed in one place:
 *   1. CRUD — add / edit / remove employees (name, photo, role, salary, etc.)
 *   2. Single-pay — quick "PAGAR ESTA SEMANA" button in each row.
 *   3. Batch-pay — "PAGAR FOLHA DA SEMANA" modal where the owner reviews the
 *      list and confirms the bulk transfer. Idempotent per ISO-week.
 */

const ROLE_OPTIONS = [
  { value: "mechanic", label: "Mecânico",  icon: Wrench },
  { value: "bdc",      label: "BDC",       icon: PhoneCall },
  { value: "detailer", label: "Detailer",  icon: Sparkles },
  { value: "office",   label: "Office",    icon: FileText },
  { value: "other",    label: "Outro",     icon: MoreHorizontal },
];

const ROLE_MAP = Object.fromEntries(ROLE_OPTIONS.map((r) => [r.value, r]));

export default function EmployeesSection({ t }) {
  const [employees, setEmployees] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // employee object or {} (new)
  const [batchOpen, setBatchOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const reload = async () => {
    try {
      const [list, sum] = await Promise.all([
        api.get("/employees"),
        api.get("/employees/payroll-summary"),
      ]);
      setEmployees(list.data || []);
      setSummary(sum.data || null);
    } catch {
      // owner-only, silently fail for non-owners
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const payOne = async (eid) => {
    try {
      const r = await api.post(`/employees/${eid}/pay-weekly`);
      if (r.data.status === "already_paid") {
        toast.info("Semana atual já paga");
      } else {
        toast.success("Salário lançado no Financeiro");
      }
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const remove = async (eid) => {
    if (!window.confirm("Remover este funcionário? Salários já pagos permanecem no histórico.")) return;
    try {
      await api.delete(`/employees/${eid}`);
      toast.success("Removido");
      await reload();
    } catch {
      toast.error("Erro ao remover");
    }
  };

  const activeEmployees = employees.filter((e) => e.active !== false);

  return (
    <div data-testid="employees-section" className="border border-border bg-background mt-8">
      {/* Header */}
      <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="label-eyebrow text-primary inline-flex items-center gap-2">
            <Users size={12} /> Funcionários (salário fixo semanal)
          </p>
          <p className="text-text-secondary text-xs mt-1">
            Mecânicos, BDC, detailers, office. Pague semanalmente sem mexer no comissionamento dos vendedores.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="employees-import"
            type="button"
            onClick={() => setImportOpen(true)}
            className="h-9 px-4 border border-border hover:border-primary hover:text-primary text-xs font-display font-bold uppercase tracking-widest inline-flex items-center gap-2"
          >
            <Users size={13} /> Importar do sistema
          </button>
          <button
            data-testid="employees-add"
            type="button"
            onClick={() => setEditing({})}
            className="h-9 px-4 bg-primary hover:bg-primary/90 text-white text-xs font-display font-bold uppercase tracking-widest inline-flex items-center gap-2"
          >
            <Plus size={13} /> Adicionar funcionário
          </button>
          {activeEmployees.length > 0 && (
            <button
              data-testid="employees-pay-batch"
              type="button"
              onClick={() => setBatchOpen(true)}
              className="h-9 px-4 border-2 border-success text-success hover:bg-success hover:text-white text-xs font-display font-bold uppercase tracking-widest inline-flex items-center gap-2"
            >
              <DollarSign size={13} /> Pagar folha da semana
            </button>
          )}
        </div>
      </div>

      {/* KPI strip */}
      {summary && (
        <div className="grid sm:grid-cols-4 gap-px bg-border">
          <KPI label="Funcionários ativos" value={activeEmployees.length} />
          <KPI label={`Folha desta semana · ${summary.iso_week}`} value={formatCurrency(summary.week_total || 0)} accent="primary" />
          <KPI label="Já pago" value={formatCurrency(summary.paid_total || 0)} accent="success" />
          <KPI label="Pendente" value={formatCurrency(summary.pending_total || 0)} accent={summary.pending_total > 0 ? "warning" : "secondary"} />
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="p-10 text-center text-text-secondary text-sm">
          <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
        </div>
      ) : employees.length === 0 ? (
        <div className="p-10 text-center text-text-secondary text-sm">
          <Briefcase size={22} className="inline mb-2 text-primary" />
          <p>Nenhum funcionário cadastrado ainda.</p>
          <p className="text-[10px] uppercase tracking-widest mt-1">
            Comece adicionando mecânicos, BDC, detailers, office, etc.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {employees.map((e) => (
            <EmployeeRow
              key={e.id}
              emp={e}
              paid={summary?.employees.find((x) => x.employee_id === e.id)?.current_week_paid}
              onEdit={() => setEditing(e)}
              onPay={() => payOne(e.id)}
              onRemove={() => remove(e.id)}
            />
          ))}
        </ul>
      )}

      {editing && (
        <EmployeeModal
          employee={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}

      {batchOpen && (
        <PayrollBatchModal
          employees={activeEmployees}
          summary={summary}
          onClose={() => setBatchOpen(false)}
          onPaid={() => { setBatchOpen(false); reload(); }}
        />
      )}

      {importOpen && (
        <ImportPickerModal
          onClose={() => setImportOpen(false)}
          onImported={() => { setImportOpen(false); reload(); }}
        />
      )}
    </div>
  );
}

// ============================================================================

function KPI({ label, value, accent = "secondary" }) {
  const color = {
    primary:   "text-primary",
    success:   "text-success",
    warning:   "text-warning",
    secondary: "text-white",
  }[accent];
  return (
    <div className="bg-background p-4">
      <p className="label-eyebrow text-text-secondary">{label}</p>
      <p className={`font-display font-black text-2xl mt-1 ${color}`}>{value}</p>
    </div>
  );
}

function EmployeeRow({ emp, paid, onEdit, onPay, onRemove }) {
  const role = ROLE_MAP[emp.role] || ROLE_MAP.other;
  const RoleIcon = role.icon;
  const inactive = emp.active === false;
  return (
    <li
      data-testid={`employee-row-${emp.id}`}
      className={`p-4 flex items-center gap-4 ${inactive ? "opacity-50" : ""}`}
    >
      {/* Avatar */}
      <div className="w-12 h-12 bg-surface border border-border overflow-hidden flex items-center justify-center shrink-0">
        {emp.photo_url ? (
          <img src={emp.photo_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <RoleIcon size={18} className="text-text-secondary" />
        )}
      </div>

      {/* Identity */}
      <div className="flex-1 min-w-0">
        <p className="font-display font-bold truncate">
          {emp.name}
          {inactive && (
            <span className="ml-2 text-[9px] uppercase tracking-widest text-text-secondary">INATIVO</span>
          )}
        </p>
        <p className="text-[10px] uppercase tracking-widest text-text-secondary mt-0.5 flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 text-primary">
            <RoleIcon size={9} /> {role.label}
          </span>
          {emp.phone && <span>· {emp.phone}</span>}
          {emp.hired_at && <span>· desde {emp.hired_at}</span>}
        </p>
      </div>

      {/* Salary */}
      <div className="text-right shrink-0 hidden sm:block">
        <p className="label-eyebrow text-text-secondary">Salário/sem</p>
        <p className="font-display font-bold text-base text-white tabular-nums">
          {formatCurrency(emp.weekly_salary || 0)}
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 shrink-0">
        {!inactive && Number(emp.weekly_salary) > 0 && (
          <button
            data-testid={`employee-pay-${emp.id}`}
            type="button"
            onClick={onPay}
            disabled={paid}
            title={paid ? "Semana atual já paga" : "Pagar salário desta semana"}
            className={`w-9 h-9 border-2 inline-flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              paid
                ? "border-success bg-success text-white"
                : "border-success text-success hover:bg-success hover:text-white"
            }`}
          >
            {paid ? <CheckCircle2 size={14} /> : <DollarSign size={14} />}
          </button>
        )}
        <button
          data-testid={`employee-edit-${emp.id}`}
          type="button"
          onClick={onEdit}
          title="Editar"
          className="w-9 h-9 border border-border text-text-secondary hover:border-primary hover:text-primary inline-flex items-center justify-center transition-colors"
        >
          <Edit2 size={14} />
        </button>
        <button
          data-testid={`employee-delete-${emp.id}`}
          type="button"
          onClick={onRemove}
          title="Remover"
          className="w-9 h-9 border border-border text-text-secondary hover:border-primary hover:text-primary inline-flex items-center justify-center transition-colors"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </li>
  );
}

// ============================================================================

function EmployeeModal({ employee, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "", role: "mechanic", weekly_salary: "", phone: "", email: "",
    hired_at: "", active: true, photo_url: "", photo_public_id: "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (employee) {
      setForm({
        name: employee.name || "",
        role: employee.role || "other",
        weekly_salary: employee.weekly_salary ?? "",
        phone: employee.phone || "",
        email: employee.email || "",
        hired_at: employee.hired_at || "",
        active: employee.active !== false,
        photo_url: employee.photo_url || "",
        photo_public_id: employee.photo_public_id || "",
      });
    }
  }, [employee]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const sigRes = await api.get("/cloudinary/signature", { params: { folder: "employees/" } });
      const { signature, timestamp, cloud_name, api_key, folder } = sigRes.data;
      const fd = new FormData();
      fd.append("file", file);
      fd.append("api_key", api_key);
      fd.append("timestamp", String(timestamp));
      fd.append("signature", signature);
      fd.append("folder", folder);
      const res = await fetch(
        `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
        { method: "POST", body: fd },
      );
      const data = await res.json();
      if (!data.secure_url) throw new Error("upload failed");
      set("photo_url", data.secure_url);
      set("photo_public_id", data.public_id || "");
    } catch {
      toast.error("Erro no upload");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Nome obrigatório");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        role: form.role,
        weekly_salary: Number(form.weekly_salary) || 0,
        phone: form.phone,
        email: form.email,
        hired_at: form.hired_at,
        active: !!form.active,
        photo_url: form.photo_url || "",
        photo_public_id: form.photo_public_id || "",
      };
      if (employee?.id) {
        await api.put(`/employees/${employee.id}`, payload);
      } else {
        await api.post("/employees", payload);
      }
      toast.success("Salvo");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-background border border-border w-full max-w-lg max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <p className="label-eyebrow text-primary">{employee?.id ? "Editar" : "Novo"}</p>
            <h3 className="font-display font-black text-xl uppercase tracking-tighter mt-1">Funcionário</h3>
          </div>
          <button type="button" onClick={onClose} className="text-text-secondary hover:text-primary">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Photo */}
          <div className="flex items-start gap-4">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="relative w-20 h-20 border border-border bg-surface flex items-center justify-center overflow-hidden hover:border-primary transition-colors"
            >
              {form.photo_url ? (
                <img src={form.photo_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <Camera size={22} className="text-text-secondary" />
              )}
              {uploading && (
                <div className="absolute inset-0 bg-background/70 flex items-center justify-center">
                  <Loader2 size={18} className="animate-spin text-primary" />
                </div>
              )}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => uploadPhoto(e.target.files?.[0])}
              data-testid="employee-photo-input"
            />
            <div className="flex-1 space-y-3">
              <Field label="Nome">
                <input
                  data-testid="employee-name"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="Ex: João da Silva"
                  className="w-full h-10 bg-surface border border-border focus:border-primary focus:outline-none px-3 text-sm"
                />
              </Field>
              <Field label="Cargo">
                <select
                  data-testid="employee-role"
                  value={form.role}
                  onChange={(e) => set("role", e.target.value)}
                  className="w-full h-10 bg-surface border border-border focus:border-primary focus:outline-none px-3 text-sm"
                >
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </Field>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Salário Semanal (US$)" icon={DollarSign}>
              <input
                data-testid="employee-salary"
                type="number"
                min="0"
                step="50"
                value={form.weekly_salary}
                onChange={(e) => set("weekly_salary", e.target.value)}
                className="w-full h-10 bg-surface border border-border focus:border-primary focus:outline-none pl-8 pr-3 text-sm"
              />
            </Field>
            <Field label="Data de Admissão" icon={CalendarDays}>
              <input
                data-testid="employee-hired-at"
                type="date"
                value={form.hired_at}
                onChange={(e) => set("hired_at", e.target.value)}
                className="w-full h-10 bg-surface border border-border focus:border-primary focus:outline-none pl-8 pr-3 text-sm"
              />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Telefone" icon={Phone}>
              <input
                data-testid="employee-phone"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                placeholder="+1 555 555-1234"
                className="w-full h-10 bg-surface border border-border focus:border-primary focus:outline-none pl-8 pr-3 text-sm"
              />
            </Field>
            <Field label="Email" icon={Mail}>
              <input
                data-testid="employee-email"
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className="w-full h-10 bg-surface border border-border focus:border-primary focus:outline-none pl-8 pr-3 text-sm"
              />
            </Field>
          </div>

          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              data-testid="employee-active"
              checked={form.active}
              onChange={(e) => set("active", e.target.checked)}
              className="w-4 h-4"
            />
            <span>Ativo (recebe folha semanal)</span>
          </label>
        </div>

        <div className="p-4 border-t border-border flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-4 border border-border text-text-secondary hover:text-primary hover:border-primary text-xs font-display font-bold uppercase tracking-widest"
          >
            Cancelar
          </button>
          <button
            data-testid="employee-save"
            type="button"
            onClick={save}
            disabled={saving}
            className="h-10 px-5 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white text-xs font-display font-bold uppercase tracking-widest inline-flex items-center gap-2"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="label-eyebrow block mb-1.5">{label}</label>
      <div className="relative">
        {Icon && (
          <Icon size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
        )}
        {children}
      </div>
    </div>
  );
}

// ============================================================================

function PayrollBatchModal({ employees, summary, onClose, onPaid }) {
  const [selected, setSelected] = useState(() => {
    // default: pre-select every active employee that hasn't been paid yet this week
    const next = new Set();
    for (const e of employees) {
      const row = summary?.employees.find((x) => x.employee_id === e.id);
      if (!row?.current_week_paid && Number(e.weekly_salary) > 0) {
        next.add(e.id);
      }
    }
    return next;
  });
  const [saving, setSaving] = useState(false);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const totalSelected = employees
    .filter((e) => selected.has(e.id))
    .reduce((s, e) => s + Number(e.weekly_salary || 0), 0);

  const confirm = async () => {
    if (selected.size === 0) {
      toast.error("Selecione pelo menos 1 funcionário");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/employees/pay-payroll", {
        employee_ids: Array.from(selected),
      });
      const { paid, already_paid, errors } = r.data;
      if (paid > 0) toast.success(`${paid} salário(s) lançados (${formatCurrency(totalSelected)} no Financeiro)`);
      if (already_paid > 0) toast.info(`${already_paid} já estavam pagos esta semana`);
      if (errors > 0) toast.error(`${errors} com erro (salário inválido ou inativo)`);
      onPaid && onPaid();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-background border border-border w-full max-w-2xl max-h-[92vh] flex flex-col"
      >
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div>
            <p className="label-eyebrow text-success">Folha semanal</p>
            <h3 className="font-display font-black text-xl uppercase tracking-tighter mt-1">
              Pagar Folha · {summary?.iso_week}
            </h3>
          </div>
          <button type="button" onClick={onClose} className="text-text-secondary hover:text-primary">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <ul className="space-y-2">
            {employees.map((e) => {
              const row = summary?.employees.find((x) => x.employee_id === e.id);
              const paid = !!row?.current_week_paid;
              const isSelected = selected.has(e.id);
              const role = ROLE_MAP[e.role] || ROLE_MAP.other;
              const RoleIcon = role.icon;
              const noSalary = !(Number(e.weekly_salary) > 0);
              const disabled = paid || noSalary;
              return (
                <li
                  key={e.id}
                  data-testid={`payroll-row-${e.id}`}
                >
                  <button
                    type="button"
                    onClick={() => !disabled && toggle(e.id)}
                    disabled={disabled}
                    className={`w-full flex items-center gap-3 p-3 border text-left transition-colors ${
                      disabled
                        ? "border-border bg-surface/40 opacity-50 cursor-not-allowed"
                        : isSelected
                        ? "border-success bg-success/10"
                        : "border-border bg-surface hover:border-success/50"
                    }`}
                  >
                    <span
                      className={`w-5 h-5 border-2 inline-flex items-center justify-center shrink-0 ${
                        isSelected ? "bg-success border-success" : "border-border"
                      }`}
                    >
                      {isSelected && <CheckCircle2 size={12} className="text-white" />}
                    </span>
                    <div className="w-9 h-9 bg-background border border-border flex items-center justify-center shrink-0">
                      {e.photo_url ? (
                        <img src={e.photo_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <RoleIcon size={14} className="text-text-secondary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-display font-bold text-sm truncate">
                        {e.name}
                        {paid && (
                          <span className="ml-2 text-[9px] uppercase tracking-widest text-success">JÁ PAGO</span>
                        )}
                        {noSalary && (
                          <span className="ml-2 text-[9px] uppercase tracking-widest text-warning">SEM SALÁRIO</span>
                        )}
                      </p>
                      <p className="text-[10px] uppercase tracking-widest text-text-secondary mt-0.5 inline-flex items-center gap-1">
                        <RoleIcon size={9} /> {role.label}
                      </p>
                    </div>
                    <p className="font-display font-bold text-sm tabular-nums">
                      {formatCurrency(e.weekly_salary || 0)}
                    </p>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="p-4 border-t border-border flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-text-secondary">
              {selected.size} selecionado(s)
            </p>
            <p className="font-display font-black text-xl text-success">
              {formatCurrency(totalSelected)}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-10 px-4 border border-border text-text-secondary hover:text-primary hover:border-primary text-xs font-display font-bold uppercase tracking-widest"
            >
              Cancelar
            </button>
            <button
              data-testid="payroll-confirm"
              type="button"
              onClick={confirm}
              disabled={saving || selected.size === 0}
              className="h-10 px-5 bg-success hover:bg-success/90 disabled:opacity-50 text-white text-xs font-display font-bold uppercase tracking-widest inline-flex items-center gap-2"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <DollarSign size={14} />}
              Lançar no financeiro
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ============================================================================

/**
 * ImportPickerModal
 * --------------------------------------------------------------------------
 * Lets the owner pull in users already registered elsewhere in the system
 * (Team users with role `geral`/`bdc`/`gerente`, and salespeople) without
 * having to retype name/email. Each row is editable inline so the owner
 * sets the weekly salary and confirms the role before importing.
 */
function ImportPickerModal({ onClose, onImported }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picks, setPicks] = useState({});      // { source_id → row state }
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/employees/importable");
        setCandidates(r.data || []);
      } catch {
        toast.error("Erro ao carregar candidatos");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const togglePick = (c) => {
    setPicks((prev) => {
      const next = { ...prev };
      if (next[c.source_id]) {
        delete next[c.source_id];
      } else {
        next[c.source_id] = {
          name: c.name,
          role: c.role || "other",
          email: c.email || "",
          phone: c.phone || "",
          photo_url: c.photo_url || "",
          weekly_salary: "",
        };
      }
      return next;
    });
  };

  const updatePick = (id, field, value) => {
    setPicks((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const picked = Object.entries(picks);

  const confirm = async () => {
    if (picked.length === 0) {
      toast.error("Selecione pelo menos 1 pessoa");
      return;
    }
    setSaving(true);
    try {
      const items = picked.map(([, p]) => ({
        ...p,
        weekly_salary: Number(p.weekly_salary) || 0,
      }));
      const r = await api.post("/employees/import", { items });
      toast.success(`${r.data.created} funcionário(s) importados`);
      onImported && onImported();
    } catch (e) {
      // Backend validation errors come as `{detail: [{...}]}` — coerce to string
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Erro ao importar";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-background border border-border w-full max-w-3xl max-h-[92vh] flex flex-col"
      >
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div>
            <p className="label-eyebrow text-primary">Importar do sistema</p>
            <h3 className="font-display font-black text-xl uppercase tracking-tighter mt-1">
              Escolher pessoas já cadastradas
            </h3>
            <p className="text-xs text-text-secondary mt-1">
              Pegamos quem você já adicionou em Equipe ou Vendedores. Defina o salário semanal e clique em importar.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-text-secondary hover:text-primary">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <p className="text-center text-text-secondary text-sm py-8">
              <Loader2 size={16} className="inline animate-spin mr-2" /> Carregando…
            </p>
          ) : candidates.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle2 size={28} className="mx-auto mb-2 text-success" />
              <p className="text-text-secondary text-sm">
                Todos os usuários da sua equipe e vendedores já estão importados.
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {candidates.map((c) => {
                const isPicked = !!picks[c.source_id];
                const pick = picks[c.source_id];
                const RoleIcon = (ROLE_MAP[c.role] || ROLE_MAP.other).icon;
                return (
                  <li
                    key={c.source_id}
                    data-testid={`import-row-${c.source_id}`}
                    className={`border p-3 transition-colors ${
                      isPicked ? "border-primary bg-primary/5" : "border-border bg-surface"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => togglePick(c)}
                        className={`w-5 h-5 border-2 inline-flex items-center justify-center shrink-0 transition-colors ${
                          isPicked ? "bg-primary border-primary" : "border-border hover:border-primary/50"
                        }`}
                      >
                        {isPicked && <CheckCircle2 size={12} className="text-white" />}
                      </button>
                      <div className="w-9 h-9 bg-background border border-border flex items-center justify-center shrink-0">
                        {c.photo_url ? (
                          <img src={c.photo_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <RoleIcon size={14} className="text-text-secondary" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-display font-bold text-sm truncate">{c.name}</p>
                        <p className="text-[10px] uppercase tracking-widest text-text-secondary mt-0.5">
                          <span className="text-primary">{c.role_source_label || c.source}</span>
                          {c.email && <> · {c.email}</>}
                        </p>
                      </div>
                    </div>
                    {/* Inline editing — only when this row is picked */}
                    {isPicked && pick && (
                      <div className="grid sm:grid-cols-2 gap-2 mt-3 pl-8">
                        <div>
                          <label className="label-eyebrow block mb-1">Cargo</label>
                          <select
                            data-testid={`import-role-${c.source_id}`}
                            value={pick.role}
                            onChange={(e) => updatePick(c.source_id, "role", e.target.value)}
                            className="w-full h-9 bg-background border border-border focus:border-primary focus:outline-none px-2 text-xs"
                          >
                            {ROLE_OPTIONS.map((r) => (
                              <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="label-eyebrow block mb-1">Salário semanal (US$)</label>
                          <input
                            data-testid={`import-salary-${c.source_id}`}
                            type="number"
                            min="0"
                            step="50"
                            value={pick.weekly_salary}
                            onChange={(e) => updatePick(c.source_id, "weekly_salary", e.target.value)}
                            placeholder="0"
                            className="w-full h-9 bg-background border border-border focus:border-primary focus:outline-none px-2 text-xs"
                          />
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="p-4 border-t border-border flex items-center justify-between flex-wrap gap-2">
          <p className="text-xs text-text-secondary">
            <strong className="text-white">{picked.length}</strong> selecionado(s)
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-10 px-4 border border-border text-text-secondary hover:text-primary hover:border-primary text-xs font-display font-bold uppercase tracking-widest"
            >
              Cancelar
            </button>
            <button
              data-testid="import-confirm"
              type="button"
              onClick={confirm}
              disabled={saving || picked.length === 0}
              className="h-10 px-5 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white text-xs font-display font-bold uppercase tracking-widest inline-flex items-center gap-2"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Importar {picked.length > 0 ? `(${picked.length})` : ""}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
